import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import * as d3 from "d3";

interface DataNode {
  name: string;
  children?: DataNode[];
  value?: number;
}

@Component({
  selector: "app-nav-mindtree",
  templateUrl: "./nav-mindtree.component.html",
  standalone: false,
  styles: [
    `
      :host { display: block; }
      .mindtree-wrap {
        width: 100%;
        height: 660px;
        background: linear-gradient(135deg, #f0f4ff 0%, #fafbff 100%);
        overflow: hidden;
        position: relative;
        border-top: 1px solid #e8edf5;
      }
      .mindtree-hint {
        position: absolute;
        bottom: 10px;
        right: 14px;
        font-size: 10px;
        color: #94a3b8;
        pointer-events: none;
        user-select: none;
        letter-spacing: 0.03em;
      }
    `,
  ],
})
export class NavMindtreeComponent implements OnChanges, AfterViewInit {
  @Input() data: DataNode | null = null;
  @ViewChild("treeContainer") treeContainer!: ElementRef;

  private initialized = false;

  ngAfterViewInit() {
    this.initialized = true;
    if (this.data) {
      // Wait for mat-tab animation to complete so container has real dimensions
      setTimeout(() => this.render(), 150);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["data"] && this.initialized) {
      setTimeout(() => this.render(), 150);
    }
  }

  private render() {
    if (!this.data || !this.treeContainer) return;
    const container = this.treeContainer.nativeElement as HTMLElement;
    const d3Any = d3 as any;

    // Clear previous render
    d3Any.select(container).selectAll("*").remove();

    const W = container.offsetWidth || container.clientWidth || 900;
    const H = 640;
    const PAD_LEFT = 70;
    const PAD_RIGHT = 20;
    const PAD_V = 40;

    // Depth colour palette  (fill, stroke, text)
    const PALETTE = [
      { fill: "#f59e0b", stroke: "#d97706", text: "#7c2d12" },  // root – amber
      { fill: "#ec4899", stroke: "#db2777", text: "#fff"     },  // L1  – pink
      { fill: "#6366f1", stroke: "#4f46e5", text: "#fff"     },  // L2  – indigo
      { fill: "#10b981", stroke: "#059669", text: "#fff"     },  // L3  – emerald
      { fill: "#06b6d4", stroke: "#0891b2", text: "#fff"     },  // L4  – cyan
    ];

    // Build hierarchy and use d3.tree().size() to fill full width & height
    const root = d3Any.hierarchy(this.data);

    // size([vertical-span, horizontal-span])
    // x = vertical position, y = horizontal position (for horizontal tree)
    d3Any
      .tree()
      .size([H - PAD_V * 2, W - PAD_LEFT - PAD_RIGHT - 30])
      .separation((a: any, b: any) =>
        (a.parent === b.parent ? 1 : 1.4) / Math.max(1, a.depth)
      )(root);

    // --- SVG ---
    const svg = d3Any
      .select(container)
      .append("svg")
      .attr("width", W)
      .attr("height", H)
      .style("cursor", "grab")
      .style("display", "block");

    // Drop-shadow filter
    const defs = svg.append("defs");
    const filt = defs
      .append("filter")
      .attr("id", "ns")
      .attr("x", "-25%").attr("y", "-40%")
      .attr("width", "150%").attr("height", "180%");
    filt.append("feDropShadow")
      .attr("dx", 0).attr("dy", 2)
      .attr("stdDeviation", 3)
      .attr("flood-color", "rgba(0,0,0,0.15)");

    // Main group – offset so root isn't clipped
    const g = svg
      .append("g")
      .attr("transform", `translate(${PAD_LEFT},${PAD_V})`);

    // --- Links ---
    g.append("g")
      .attr("fill", "none")
      .selectAll("path")
      .data(root.links())
      .join("path")
      .attr("stroke", (d: any) => PALETTE[d.target.depth % PALETTE.length].stroke)
      .attr("stroke-opacity", 0.45)
      .attr("stroke-width", (d: any) => Math.max(1, 2.8 - d.target.depth * 0.55))
      .attr(
        "d",
        d3Any
          .linkHorizontal()
          .x((d: any) => d.y)   // y → horizontal
          .y((d: any) => d.x)   // x → vertical
      );

    // --- Node helpers ---
    const nw = (d: any): number => {
      if (d.depth === 0) return 110;
      return Math.max(80, Math.min(d.data.name.length * 7.5 + 26, 175));
    };
    const nh = (d: any): number => (d.depth === 0 ? 44 : 30);
    const truncate = (name: string, maxCh: number) =>
      name.length > maxCh ? name.slice(0, maxCh) + "\u2026" : name;

    const nodeG = g
      .append("g")
      .selectAll("g")
      .data(root.descendants())
      .join("g")
      .attr("transform", (d: any) => `translate(${d.y},${d.x})`);

    // Root oval
    nodeG
      .filter((d: any) => d.depth === 0)
      .append("ellipse")
      .attr("rx", 55).attr("ry", 23)
      .attr("fill", PALETTE[0].fill)
      .attr("stroke", PALETTE[0].stroke)
      .attr("stroke-width", 2.5)
      .attr("filter", "url(#ns)");

    // Branch / leaf rounded-rect
    nodeG
      .filter((d: any) => d.depth > 0)
      .append("rect")
      .attr("x", (d: any) => -nw(d) / 2)
      .attr("y", (d: any) => -nh(d) / 2)
      .attr("width", nw)
      .attr("height", nh)
      .attr("rx", (d: any) => nh(d) / 2)
      .attr("fill", (d: any) => PALETTE[d.depth % PALETTE.length].fill)
      .attr("stroke", (d: any) => PALETTE[d.depth % PALETTE.length].stroke)
      .attr("stroke-width", 1.5)
      .attr("filter", "url(#ns)");

    // Shine stripe
    nodeG
      .filter((d: any) => d.depth > 0)
      .append("rect")
      .attr("x", (d: any) => -nw(d) / 2 + 5)
      .attr("y", (d: any) => -nh(d) / 2 + 2)
      .attr("width", (d: any) => nw(d) - 10)
      .attr("height", (d: any) => nh(d) / 2 - 2)
      .attr("rx", (d: any) => nh(d) / 2 - 1)
      .attr("fill", "rgba(255,255,255,0.2)")
      .style("pointer-events", "none");

    // Helper: sum all leaf values under a node
    const sumVisits = (d: any): number => {
      if (!d.children || d.children.length === 0) return d.data.value || 0;
      return d.children.reduce((acc: number, c: any) => acc + sumVisits(c), 0);
    };

    const fmtCount = (n: number) => n > 999 ? (n / 1000).toFixed(1) + "k" : String(n);

    // Visit-count badge on ALL non-root nodes that have a total > 0
    const badge = nodeG
      .filter((d: any) => d.depth > 0 && sumVisits(d) > 0);

    badge
      .append("circle")
      .attr("cx", (d: any) => nw(d) / 2 + 2)
      .attr("cy", (d: any) => -nh(d) / 2 + 1)
      .attr("r", 10)
      .attr("fill", "#0f172a")
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5);

    badge
      .append("text")
      .attr("x", (d: any) => nw(d) / 2 + 2)
      .attr("y", (d: any) => -nh(d) / 2 + 1)
      .attr("dy", "0.35em")
      .attr("text-anchor", "middle")
      .attr("fill", "#fff")
      .style("font-size", "8px")
      .style("font-weight", "700")
      .style("pointer-events", "none")
      .text((d: any) => fmtCount(sumVisits(d)));

    // Labels
    nodeG
      .append("text")
      .attr("dy", "0.35em")
      .attr("text-anchor", "middle")
      .attr("fill", (d: any) => PALETTE[d.depth % PALETTE.length].text)
      .style("font-size", (d: any) => (d.depth === 0 ? "13px" : d.depth === 1 ? "12px" : "11px"))
      .style("font-weight", (d: any) => (d.depth <= 1 ? "700" : "600"))
      .style("pointer-events", "none")
      .style("user-select", "none")
      .text((d: any) => {
        const maxCh = Math.floor((nw(d) - 20) / 6.5);
        return truncate(d.data.name, maxCh);
      });

    // --- Tooltip ---
    const tt = d3Any
      .select(container)
      .append("div")
      .style("position", "absolute")
      .style("background", "rgba(15,23,42,0.92)")
      .style("color", "#fff")
      .style("padding", "7px 13px")
      .style("border-radius", "8px")
      .style("font-size", "12px")
      .style("line-height", "1.6")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("white-space", "nowrap")
      .style("box-shadow", "0 6px 18px rgba(0,0,0,0.22)")
      .style("z-index", 20)
      .style("transition", "opacity 0.12s");

    nodeG
      .on("mouseover", (_event: any, d: any) => {
        const val =
          !d.children && d.data.value
            ? `<br><span style="color:#94a3b8;font-size:10px">${d.data.value} visits</span>`
            : "";
        tt.style("opacity", 1).html(`<strong>${d.data.name}</strong>${val}`);
      })
      .on("mousemove", (event: any) => {
        const rect = container.getBoundingClientRect();
        tt
          .style("left", event.clientX - rect.left + 14 + "px")
          .style("top", event.clientY - rect.top - 44 + "px");
      })
      .on("mouseleave", () => tt.style("opacity", 0));

    // --- Zoom / Pan ---
    const zoom = d3Any
      .zoom()
      .scaleExtent([0.05, 4])
      .on("zoom", (event: any) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);
    svg
      .on("mousedown.cur", () => svg.style("cursor", "grabbing"))
      .on("mouseup.cur", () => svg.style("cursor", "grab"));

    // Initial fit: centre the tree and scale if it overflows
    const bbox = (g.node() as SVGGElement).getBBox();
    const scaleX = W / (bbox.width + 80);
    const scaleY = H / (bbox.height + PAD_V * 2);
    const scale = Math.min(scaleX, scaleY, 1);
    const tx = (W - bbox.width * scale) / 2 - bbox.x * scale;
    const ty = (H - bbox.height * scale) / 2 - bbox.y * scale;

    svg.call(
      zoom.transform,
      d3Any.zoomIdentity.translate(tx, ty).scale(scale)
    );
  }
}
